// © 2026 Camp Promethean, LLC. All Rights Reserved.
// Phase 20 / GL-6 — per-room Growlink configuration: choose which controller sensors feed which
// Aura series (metric + zone), which devices mean irrigation/lights (GL-8 truth), and the lights
// on/off window. Discovery and live preview go through growlink-proxy; mappings are written
// straight to growlink_sensor_map / growlink_device_map (RLS: integration admins).

import { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, AlertCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { readPhotoperiod } from "@/lib/aroyaPhotoperiod";

export interface GlConfigZone { id: string; zone_number: number }
export interface GlConfigRoom {
  id: string; facility_id: string; room_name: string;
  growlink_room_id: string | null; photoperiod: unknown; zones: GlConfigZone[];
}

interface Suggestion { metric: string; level: "room" | "zone"; role?: string; needsRole?: boolean }
interface DiscoveredSensor {
  id: string; name: string; metric: number; unitOfMeasure: number; sensorType: number;
  moduleId: string | null; moduleName: string | null; suggestion: Suggestion | null;
}
interface DiscoveredDevice { id: string; name: string; deviceType: number; interfaceType: number; suggestedRole: DeviceRole }
interface LiveReading { sensorId: string; value: number; suffix?: string; timestamp: string }
interface MetricDef { key: string; label: string; unit: string | null; domain: string; default_level: "room" | "zone" }

type DeviceRole = "irrigation" | "light" | "climate" | "fertigation" | "other";
const DEVICE_ROLES: DeviceRole[] = ["irrigation", "light", "climate", "fertigation", "other"];
const SKIP = "__skip__";
const NO_ZONE = "__none__";
const ZONE_METRICS = new Set(["soil_moist", "pore_ec", "soil_temp"]);
const NON_TELEMETRY = new Set([255, 11, 7, 15]); // None / raw voltage inputs / battery / switch state
const TEMP_METRIC = 0;
const roleFor = (metric: string) => metric === "soil_temp" ? "substrate" : metric === "water_temp" ? "water" : metric === "air_temp" ? "air" : null;

const GL_METRIC_LABEL: Record<number, string> = {
  0: "Temperature", 1: "Humidity", 2: "pH", 3: "CO₂", 6: "Light level", 8: "VPD", 9: "Water content",
  10: "EC", 13: "Dissolved O₂", 14: "Flow rate", 20: "PAR", 22: "DLI", 29: "Tank level", 30: "Tank volume", 35: "Lights on/off",
};
const DEVICE_TYPE_LABEL: Record<number, string> = {
  0: "Unassigned output", 1: "Reservoir chiller", 2: "Reservoir pump", 3: "Light", 4: "Fan", 5: "Heater", 6: "Air conditioner",
  7: "Humidifier", 8: "Dehumidifier", 9: "CO₂ burner", 10: "CO₂ regulator", 11: "Exhaust fan", 12: "Irrigation", 13: "Dosing pump",
  14: "Reservoir fill", 15: "Reservoir drain", 16: "Not in use", 17: "Light (analog)", 18: "Light (PWM)", 19: "Dosing pump (inline)",
  20: "Motor vents", 21: "Thermal screen", 22: "Shading screen", 23: "Pad pump", 24: "Valve", 25: "Exclusive valve",
  26: "Light (HPS)", 27: "Light (CMH)", 28: "Light (LED)", 29: "Batch tank",
};

export function GrowlinkRoomConfigDialog({ room, open, onOpenChange, onSaved }: {
  room: GlConfigRoom | null; open: boolean; onOpenChange: (v: boolean) => void; onSaved: () => void;
}) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sensors, setSensors] = useState<DiscoveredSensor[]>([]);
  const [devices, setDevices] = useState<DiscoveredDevice[]>([]);
  const [defs, setDefs] = useState<MetricDef[]>([]);
  const [live, setLive] = useState<Record<string, LiveReading>>({});
  const [sensorSel, setSensorSel] = useState<Record<string, { metric: string; zone_id: string | null }>>({});
  const [existingSensorIds, setExistingSensorIds] = useState<Set<string>>(new Set());
  const [devSel, setDevSel] = useState<Record<string, { role: DeviceRole; zone_id: string | null; enabled: boolean }>>({});
  const [photo, setPhoto] = useState({ on: "08:00", off: "20:00" });

  useEffect(() => {
    if (!open || !room) return;
    setPhoto(readPhotoperiod(room.photoperiod));
    setLoadError(null); setSensors([]); setDevices([]); setLive({}); setSensorSel({}); setDevSel({});
    if (!room.growlink_room_id) { setLoadError("Map this room to a controller room first, then reopen to pick sensors."); return; }
    const db = supabase as any;
    (async () => {
      setLoading(true);
      try {
        const [sRes, dRes, mapRes, devMapRes, defRes] = await Promise.all([
          supabase.functions.invoke("growlink-proxy", { body: { action: "sensors", facilityId: room.facility_id, roomId: room.growlink_room_id } }),
          supabase.functions.invoke("growlink-proxy", { body: { action: "devices", facilityId: room.facility_id, roomId: room.growlink_room_id } }),
          db.from("growlink_sensor_map").select("sensor_id, metric, level, zone_id, enabled").eq("room_id", room.id),
          db.from("growlink_device_map").select("device_id, role, zone_id, enabled").eq("room_id", room.id),
          db.from("aroya_metric_defs").select("key, label, unit, domain, default_level").order("domain").order("label"),
        ]);
        if (sRes.error) throw sRes.error;
        if (!sRes.data?.ok) throw new Error(sRes.data?.error || "Could not list controller sensors");
        const discovered: DiscoveredSensor[] = sRes.data.data ?? [];
        const discoveredDevices: DiscoveredDevice[] = dRes.data?.ok ? (dRes.data.data ?? []) : [];
        setSensors(discovered); setDevices(discoveredDevices);
        setDefs((defRes.data ?? []) as MetricDef[]);

        const existing: Record<string, { metric: string; zone_id: string | null }> = {};
        const ids = new Set<string>();
        for (const m of (mapRes.data ?? []) as any[]) { ids.add(m.sensor_id); existing[m.sensor_id] = { metric: m.enabled ? m.metric : SKIP, zone_id: m.zone_id ?? null }; }
        setExistingSensorIds(ids);
        const sel: Record<string, { metric: string; zone_id: string | null }> = {};
        for (const s of discovered) {
          if (existing[s.id]) { sel[s.id] = existing[s.id]; continue; }
          if (NON_TELEMETRY.has(s.metric) || !s.suggestion) { sel[s.id] = { metric: SKIP, zone_id: null }; continue; }
          sel[s.id] = { metric: s.suggestion.metric, zone_id: null };
        }
        setSensorSel(sel);

        const existingDev: Record<string, { role: DeviceRole; zone_id: string | null; enabled: boolean }> = {};
        for (const d of (devMapRes.data ?? []) as any[]) existingDev[d.device_id] = { role: d.role, zone_id: d.zone_id ?? null, enabled: d.enabled };
        const dsel: typeof existingDev = {};
        for (const d of discoveredDevices) {
          dsel[d.id] = existingDev[d.id] ?? { role: d.suggestedRole, zone_id: guessZone(d.name, room.zones), enabled: d.suggestedRole !== "other" };
        }
        setDevSel(dsel);

        // Live preview (best effort).
        const liveRes = await supabase.functions.invoke("growlink-proxy", {
          body: { action: "live_sensors", facilityId: room.facility_id, sensorIds: discovered.filter((s) => !NON_TELEMETRY.has(s.metric)).map((s) => s.id) },
        });
        if (liveRes.data?.ok) setLive(Object.fromEntries(((liveRes.data.data ?? []) as LiveReading[]).map((r) => [String(r.sensorId).toLowerCase(), r])));
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [open, room]);

  const telemetry = useMemo(() => sensors.filter((s) => !NON_TELEMETRY.has(s.metric)), [sensors]);
  const skipped = sensors.length - telemetry.length;
  const defByKey = useMemo(() => Object.fromEntries(defs.map((d) => [d.key, d])), [defs]);
  const metricOptions = (s: DiscoveredSensor): MetricDef[] => {
    // Temperature needs a role → the three temp keys; everything else offers the whole dictionary
    // with the suggestion first, so an odd sensor can still be imported under the right key.
    if (s.metric === TEMP_METRIC) return ["air_temp", "soil_temp", "water_temp"].map((k) => defByKey[k]).filter(Boolean);
    const sug = s.suggestion?.metric;
    return [...defs].sort((a, b) => (a.key === sug ? -1 : b.key === sug ? 1 : 0));
  };

  const save = async () => {
    if (!room) return;
    const db = supabase as any;
    // Validate zone-level picks.
    for (const s of telemetry) {
      const sel = sensorSel[s.id]; if (!sel || sel.metric === SKIP) continue;
      if (ZONE_METRICS.has(sel.metric) && !sel.zone_id) {
        toast({ variant: "destructive", title: "Zone required", description: `${s.name}: ${defByKey[sel.metric]?.label ?? sel.metric} is a per-zone series — pick the zone this probe sits in.` });
        return;
      }
    }
    setSaving(true);
    try {
      for (const s of sensors) {
        const sel = sensorSel[s.id];
        if (!sel || sel.metric === SKIP) {
          if (existingSensorIds.has(s.id)) { const { error } = await db.from("growlink_sensor_map").delete().eq("sensor_id", s.id); if (error) throw error; }
          continue;
        }
        const level = ZONE_METRICS.has(sel.metric) ? "zone" : "room";
        const { error } = await db.from("growlink_sensor_map").upsert({
          facility_id: room.facility_id, room_id: room.id, sensor_id: s.id, sensor_name: s.name,
          module_id: s.moduleId, module_name: s.moduleName, gl_metric: s.metric, gl_uom: s.unitOfMeasure,
          metric: sel.metric, level, zone_id: level === "zone" ? sel.zone_id : null, role: roleFor(sel.metric), enabled: true,
        }, { onConflict: "sensor_id" });
        if (error) throw error;
      }
      for (const d of devices) {
        const sel = devSel[d.id]; if (!sel) continue;
        const { error } = await db.from("growlink_device_map").upsert({
          facility_id: room.facility_id, room_id: room.id, device_id: d.id, device_name: d.name,
          device_type: d.deviceType, interface_type: d.interfaceType, role: sel.role,
          zone_id: sel.role === "irrigation" ? sel.zone_id : null, enabled: sel.enabled,
        }, { onConflict: "device_id" });
        if (error) throw error;
      }
      const { error: pErr } = await supabase.from("rooms").update({ photoperiod: { on: photo.on, off: photo.off } as never }).eq("id", room.id);
      if (pErr) throw pErr;
      toast({ title: "Room configured", description: `${room.room_name}: sensors, devices and photoperiod saved.` });
      onSaved(); onOpenChange(false);
    } catch (e: any) {
      toast({ variant: "destructive", title: "Save failed", description: e?.message || "Could not save room configuration." });
    } finally {
      setSaving(false);
    }
  };

  const fmtLive = (s: DiscoveredSensor) => {
    const r = live[s.id.toLowerCase()];
    return r ? `${r.value}${r.suffix ? " " + r.suffix : ""}` : "—";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(90vh,820px)] max-w-3xl flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="flex-shrink-0 space-y-1.5 px-6 pt-6">
          <DialogTitle>Configure {room?.room_name}</DialogTitle>
          <DialogDescription>
            Choose which controller sensors feed this room's readings, which devices are irrigation and lights, and the lights on/off window.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {loadError && (
            <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-sm text-muted-foreground">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" /><span>{loadError}</span>
            </div>
          )}
          {loading ? (
            <div className="flex items-center gap-2 py-6 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Reading the controller…</div>
          ) : !loadError && (
            <>
              <section>
                <div className="flex items-baseline justify-between">
                  <Label className="text-sm font-medium">Sensors</Label>
                  <span className="text-xs text-muted-foreground">
                    {telemetry.length} importable{skipped ? ` · ${skipped} raw inputs hidden` : ""}
                  </span>
                </div>
                <p className="mb-2 text-xs text-muted-foreground">
                  Averaged sensors from the controller are preferred over their individual probes. Several sensors sent to one series are averaged.
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sensor</TableHead>
                      <TableHead className="w-[110px]">Live</TableHead>
                      <TableHead className="w-[220px]">Import as</TableHead>
                      <TableHead className="w-[150px]">Zone</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {telemetry.map((s) => {
                      const sel = sensorSel[s.id] ?? { metric: SKIP, zone_id: null };
                      const zoneLevel = sel.metric !== SKIP && ZONE_METRICS.has(sel.metric);
                      return (
                        <TableRow key={s.id}>
                          <TableCell>
                            <div className="font-medium">{s.name}</div>
                            <div className="text-xs text-muted-foreground">{GL_METRIC_LABEL[s.metric] ?? `metric ${s.metric}`}{s.moduleName ? ` · ${s.moduleName}` : ""}</div>
                          </TableCell>
                          <TableCell className="text-xs tabular-nums">{fmtLive(s)}</TableCell>
                          <TableCell>
                            <Select value={sel.metric} onValueChange={(v) => setSensorSel((m) => ({ ...m, [s.id]: { metric: v, zone_id: m[s.id]?.zone_id ?? null } }))}>
                              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value={SKIP}>Don't import</SelectItem>
                                {metricOptions(s).map((d) => (
                                  <SelectItem key={d.key} value={d.key}>{d.label}{d.unit ? ` (${d.unit})` : ""}{s.suggestion?.metric === d.key ? " · suggested" : ""}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {zoneLevel ? (
                              <Select value={sel.zone_id ?? NO_ZONE} onValueChange={(v) => setSensorSel((m) => ({ ...m, [s.id]: { metric: m[s.id]?.metric ?? SKIP, zone_id: v === NO_ZONE ? null : v } }))}>
                                <SelectTrigger className="w-full"><SelectValue placeholder="Pick zone" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={NO_ZONE}>Pick zone…</SelectItem>
                                  {room?.zones.map((z) => <SelectItem key={z.id} value={z.id}>Zone {z.zone_number}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            ) : <span className="text-xs text-muted-foreground">{sel.metric === SKIP ? "" : "room"}</span>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {telemetry.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-muted-foreground">The controller reports no importable sensors in this room.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </section>

              <section>
                <Label className="text-sm font-medium">Devices</Label>
                <p className="mb-2 text-xs text-muted-foreground">
                  Irrigation valves become the source of truth for shots per zone; lights become the observed photoperiod. Read-only — nothing here controls hardware.
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Device</TableHead>
                      <TableHead className="w-[150px]">Role</TableHead>
                      <TableHead className="w-[140px]">Zone</TableHead>
                      <TableHead className="w-[80px]">Track</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {devices.map((d) => {
                      const sel = devSel[d.id] ?? { role: "other" as DeviceRole, zone_id: null, enabled: false };
                      return (
                        <TableRow key={d.id}>
                          <TableCell>
                            <div className="font-medium">{d.name}</div>
                            <div className="text-xs text-muted-foreground">{DEVICE_TYPE_LABEL[d.deviceType] ?? `type ${d.deviceType}`}</div>
                          </TableCell>
                          <TableCell>
                            <Select value={sel.role} onValueChange={(v) => setDevSel((m) => ({ ...m, [d.id]: { ...sel, role: v as DeviceRole } }))}>
                              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                              <SelectContent>{DEVICE_ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            {sel.role === "irrigation" ? (
                              <Select value={sel.zone_id ?? NO_ZONE} onValueChange={(v) => setDevSel((m) => ({ ...m, [d.id]: { ...sel, zone_id: v === NO_ZONE ? null : v } }))}>
                                <SelectTrigger className="w-full"><SelectValue placeholder="Zone" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value={NO_ZONE}>Whole room</SelectItem>
                                  {room?.zones.map((z) => <SelectItem key={z.id} value={z.id}>Zone {z.zone_number}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            ) : <span className="text-xs text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell>
                            <Switch checked={sel.enabled} onCheckedChange={(v) => setDevSel((m) => ({ ...m, [d.id]: { ...sel, enabled: v } }))} />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {devices.length === 0 && (
                      <TableRow><TableCell colSpan={4} className="text-muted-foreground">No devices in this controller room.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </section>

              <section>
                <Label className="text-sm font-medium">Photoperiod (lights)</Label>
                <p className="mb-2 text-xs text-muted-foreground">
                  Used for day/night averages and chart shading until observed light runs take over. Times are in your facility timezone.
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">On</span>
                    <Input type="time" value={photo.on} onChange={(e) => setPhoto((p) => ({ ...p, on: e.target.value }))} className="w-32" /></div>
                  <div className="flex items-center gap-2"><span className="text-sm text-muted-foreground">Off</span>
                    <Input type="time" value={photo.off} onChange={(e) => setPhoto((p) => ({ ...p, off: e.target.value }))} className="w-32" /></div>
                  {photo.off <= photo.on && <Badge variant="secondary">overnight</Badge>}
                </div>
              </section>
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading || !!loadError}>
            {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving</> : "Save configuration"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "Zone 3" / "Zone 3 Valve" → our zone 3, when such a zone exists. */
function guessZone(deviceName: string, zones: GlConfigZone[]): string | null {
  const m = /zone\s*(\d+)/i.exec(deviceName);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return zones.find((z) => z.zone_number === n)?.id ?? null;
}
